/**
 * src/hooks/useFeedbackTrigger.js
 *
 * Client-side hook that manages the timed feedback/rating popup logic:
 *
 *  1. Tracks user active time (10 minutes threshold)
 *  2. After 10 minutes, schedules a feedback popup 24 hours later (via Firestore)
 *  3. Listens for pending popups and surfaces them when due
 *  4. Also listens for error events to trigger ErrorReportPopup
 *
 * Uses localStorage to avoid re-scheduling on every session.
 * Works on Firebase Spark plan — pure client-side + Firestore.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { errorDev } from '../utils/logger.js';
import {
  scheduleFeedbackPopup,
  listenPendingPopups,
} from "../services/feedbackService";

const ACTIVE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes
const SCHEDULE_COOLDOWN_KEY = "orrith_feedback_scheduled_at";
const COOLDOWN_HOURS = 72; // Don't re-schedule within 72 hours

export default function useFeedbackTrigger(user) {
  const [currentPopup, setCurrentPopup] = useState(null);
  const [errorToReport, setErrorToReport] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const activeTimeRef = useRef(0);
  const scheduledRef = useRef(false);
  const intervalRef = useRef(null);
  const lastErrorRef = useRef({ key: "", at: 0 });

  const toUserFriendlyMessage = useCallback((raw) => {
    const msg = String(raw || "");
    if (!msg) return "Something went wrong while loading this page.";
    const lower = msg.toLowerCase();

    if (lower.includes("missing or insufficient permissions") || lower.includes("permission_denied")) {
      return "We could not access some data right now. Please refresh and try again.";
    }
    if (lower.includes("network") || lower.includes("failed to fetch")) {
      return "Network issue detected. Please check your connection and try again.";
    }
    if (lower.includes("timeout") || lower.includes("deadline")) {
      return "The request took too long. Please try again.";
    }
    return "Something unexpected happened. Please try again.";
  }, []);

  const trySetError = useCallback((payload) => {
    const key = `${payload.message}|${window.location.pathname}`;
    const now = Date.now();
    const recent = lastErrorRef.current;

    // Drop noisy duplicates that can cause popup flicker.
    if (recent.key === key && now - recent.at < 10000) return;
    if (errorToReport && now - recent.at < 3000) return;

    lastErrorRef.current = { key, at: now };
    setErrorToReport(payload);
  }, [errorToReport]);

  // ─── Track active time & schedule popup ───────────────────────
  useEffect(() => {
    if (!user) return;

    // Check cooldown
    try {
      const lastScheduled = localStorage.getItem(SCHEDULE_COOLDOWN_KEY);
      if (lastScheduled) {
        const hoursSince =
          (Date.now() - parseInt(lastScheduled, 10)) / (1000 * 60 * 60);
        if (hoursSince < COOLDOWN_HOURS) {
          scheduledRef.current = true; 
        }
      }
    } catch (err) {
      // localStorage unavailable — still continue
    errorDev("Operation failed:", err);
    }

    // Increment active time every 30 seconds
    intervalRef.current = setInterval(() => {
      if (document.visibilityState === "visible") {
        activeTimeRef.current += 30000;

        // After 10 minutes of activity, schedule the feedback popup
        if (
          activeTimeRef.current >= ACTIVE_THRESHOLD_MS &&
          !scheduledRef.current
        ) {
          scheduledRef.current = true;
          scheduleFeedbackPopup(user.uid)
            .then(() => {
              try {
                localStorage.setItem(
                  SCHEDULE_COOLDOWN_KEY,
                  Date.now().toString()
                );
              } catch (err) {
                // Silent
              errorDev("Operation failed:", err);
              }
            })
            .catch(() => {
              // Silent  non-critical
            });
        }
      }
    }, 30000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user]);

  // ─── Listen for pending popups (real-time) ────────────────────
  useEffect(() => {
    if (!user) return;

    const unsub = listenPendingPopups(user.uid, (popups) => {
      if (popups.length > 0 && !currentPopup) {
        setCurrentPopup(popups[0]); // Show one at a time
      }
    });

    return () => unsub();
  }, [currentPopup, user]);

  // ─── Global error listener for ErrorReportPopup ───────────────
  useEffect(() => {
    const handleError = (event) => {
      // Only capture unhandled errors for logged-in users
    if (!user) return;
    trySetError({
      message: toUserFriendlyMessage(event.error?.message || event.message || "Unknown error"),
      stack: event.error?.stack || "",
    });
  };

  const handleUnhandledRejection = (event) => {
    if (!user) return;
      trySetError({
        message: toUserFriendlyMessage(
          event.reason?.message ||
          String(event.reason) ||
          "Unhandled promise rejection"
        ),
        stack: event.reason?.stack || "",
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
   }, [toUserFriendlyMessage, trySetError, user]);

  // ─── Callbacks ────────────────────────────────────────────────
  const dismissCurrentPopup = useCallback(() => {
    setCurrentPopup(null);
  }, []);

  const dismissError = useCallback(() => {
    setErrorToReport(null);
  }, []);

  const openFeedbackModal = useCallback(() => {
    setShowFeedbackModal(true);
  }, []);

  const closeFeedbackModal = useCallback(() => {
    setShowFeedbackModal(false);
  }, []);

  return {
    currentPopup,
    dismissCurrentPopup,
    errorToReport,
    dismissError,
    showFeedbackModal,
    openFeedbackModal,
    closeFeedbackModal,
  };
}
